import React from 'react';
import { X, BookOpen } from 'lucide-react';

// ────────────────────────────────────────────────────────────────────
// Scoring Rubric (Feb 2026)
//
// The authoritative 9-dimension × 5-band scoring matrix. Same rubric
// used by the AI enrichment pipeline AND the human reviewer committee
// so both are anchored to the same yardstick. Shown wherever a
// reviewer needs to eyeball "what does a 3 mean?" without leaving the
// screen — Cohort Scoring Matrix, Deep Dive modal (Scoring tab),
// Executive Summary modal.
//
// If backend `SCORING_CRITERIA` in /app/backend/services/viatris_assessment.py
// changes (dims renamed / reordered), mirror it here. Bands are
// verbatim from the Viatris-approved rubric card (Feb 2026 meeting).
// ────────────────────────────────────────────────────────────────────

export const SCORING_RUBRIC = [
  { key: 'prevalence',                  label: 'Prevalence',
    bands: ['Very low', 'Low', 'Medium', 'High', 'Very high'],
    hint:  'Patient population size in the target indication / geography.' },
  { key: 'unmet_need',                  label: 'Unmet Need',
    bands: ['Worse than Standard of care', 'Same as SoC', 'Same with better convenience',
            'Better safety / convenience', 'Better efficacy / safety / convenience'],
    hint:  'How the asset improves on the current standard of care.' },
  { key: 'efficacy',                    label: 'Efficacy',
    bands: ['Inferior (vs close competitor)', 'Slightly lower', 'Equal', 'Slightly higher', 'Superior'],
    hint:  'Head-to-head efficacy read vs the nearest active competitor.' },
  { key: 'safety',                      label: 'Safety',
    bands: ['Harmful (vs close competitor)', 'Slightly worse', 'Comparable',
            'Slightly better', 'Superior safety'],
    hint:  'Head-to-head safety / tolerability read vs the nearest active competitor.' },
  { key: 'competitive_differentiation', label: 'Competitive Landscape',
    bands: ['Crowded, no differentiation', 'Crowded, limited diff.', 'Crowded, clear diff.',
            'Less crowded', 'Uncrowded'],
    hint:  'Competitive density in the target market AND how differentiated the asset is.' },
  { key: 'time_to_launch',              label: 'Time to Launch',
    bands: ['Phase 3 positive', 'Submitted', 'Pre-approval', 'Approved', 'Marketed'],
    hint:  'Regulatory / clinical development stage — nearer to launch scores higher.' },
  { key: 'strategic_alignment',         label: 'Strategic Alignment',
    bands: ['Related TA (country)', 'Related TA (region)', 'Priority TA (country)',
            'Priority TA (region)', 'Priority TA (global)'],
    hint:  'Fit with the client\u2019s declared therapeutic-area priorities and geographic footprint.' },
  { key: 'remaining_investment',        label: 'Development Cost',
    bands: ['> 50 M USD', '20 – 50 M USD', '< 20 M USD', 'RWE only', 'No incremental cost'],
    hint:  'Estimated incremental R&D spend to reach launch (lower is better).' },
  { key: 'clinical_adoption',           label: 'Adoption in Clinical Practice / Guideline Potential',
    bands: ['Not feasible', 'Low prob., 2nd line', 'Low prob., 1st line',
            'High prob., 2nd line', 'High prob., 1st line'],
    hint:  'Likelihood of guideline / KOL adoption at 1st or 2nd line.' },
];


export default function ScoringRubricModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="scoring-rubric-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-br from-indigo-50/60 to-white flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-700 font-semibold inline-flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" /> Reference · Scoring Rubric
            </div>
            <h2 className="text-xl font-bold text-slate-800 mt-1">
              The 9 dimensions · what each score means
            </h2>
            <p className="text-xs text-slate-600 mt-1 max-w-3xl">
              Every asset in the Viatris cohort is scored 1–5 on each of these nine dimensions
              — by the AI enrichment pipeline first, then verified by the six-role committee.
              The primary metric is the <strong>weighted average / 5</strong> across scored dimensions,
              so partial scoring (a specialist only rating their own domain) is treated fairly.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="scoring-rubric-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm" data-testid="scoring-rubric-table">
              <thead>
                <tr className="bg-indigo-700 text-white text-left">
                  <th className="px-3 py-2.5 font-semibold w-48">Criterion</th>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <th key={n} className="px-3 py-2.5 font-semibold text-center w-40">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/25 mr-1">
                        {n}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SCORING_RUBRIC.map((row, ri) => (
                  <tr
                    key={row.key}
                    className={ri % 2 === 0 ? 'bg-slate-50/60' : 'bg-white'}
                    data-testid={`scoring-rubric-row-${row.key}`}
                  >
                    <td className="px-3 py-2.5 font-semibold text-slate-800 border-r border-slate-200 align-top">
                      <div>{row.label}</div>
                      <div className="text-[10px] font-normal text-slate-500 mt-0.5 leading-snug">
                        {row.hint}
                      </div>
                    </td>
                    {row.bands.map((b, i) => (
                      <td
                        key={i}
                        className="px-3 py-2.5 text-slate-700 border-r border-slate-100 text-xs align-top leading-snug"
                      >
                        {b}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footnotes */}
          <div className="mt-5 grid md:grid-cols-2 gap-4 text-xs text-slate-600">
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
              <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">
                How the weighted-average score is derived
              </div>
              Each of the 9 dimensions is scored 1–5. The <strong>weighted average / 5</strong> is
              the mean over every scored vote — so a specialist who only rates their own
              dimension doesn&apos;t drag the score down with unscored zeros.
              Math tier anchors: Strategic ≥ 4.5 · High 3.75 – 4.5 · Medium 3.0 – 3.75 ·
              Monitor 2.0 – 3.0 · Low &lt; 2.0.
            </div>
            <div className="border border-slate-200 rounded-lg p-3 bg-amber-50/40">
              <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1">
                Why displayed tier can differ from math
              </div>
              Displayed tier is an <strong>AI-holistic call</strong> — IP risk, competitive crowding,
              commercial fit, and modality alignment can push an asset up or down its math band.
              Every deviation carries a rationale you can see via the <em>(i)</em> chip next to
              the tier badge on the Reference Library and Cohort Scoring Matrix.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex items-center justify-between">
          <span>Rubric agreed with Viatris · Feb 2026 · shared across AI pipeline + committee reviewers.</span>
          <button
            onClick={onClose}
            className="text-slate-600 hover:text-slate-900 font-medium"
            data-testid="scoring-rubric-close-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
